import React, { useCallback } from 'react';
import max from 'date-fns/max';
import subDays from 'date-fns/subDays';
import { ff } from '@atlassian/jira-feature-flagging';
import { ErrorFlag } from '@atlassian/jira-issue-error-flag';
import { FireScreenAnalytics } from '@atlassian/jira-product-analytics-bridge';
import type { IssueId } from '@atlassian/jira-shared-types';
import Table from '@atlassian/jira-software-deployments-common-table/src/components/table';
import type { Components } from '@atlassian/jira-software-deployments-common-table/src/components/table/components';
import { getPositionForTimestamp } from '@atlassian/jira-software-deployments-common-table/src/utils';
import { LoadingStateOptions } from '@atlassian/jira-software-deployments-service-query-types';
import type { Partial } from '../../common/types';
import { startOfDay } from '../../common/utils/dates';
import { getTimelineDurationFromTimeWindowFilter } from '../../common/utils/timeline-duration';
import { useTimeWindowFilter } from '../../controllers/date-filter/utils';
import { useSidebarActions, useSidebarIssueKey } from '../../controllers/sidebar';
import { useTimelineMode } from '../../controllers/timeline-mode';
import { useIssues } from '../../services/core-data';
import { useEarliestVisibleDeploymentDate } from '../../services/core-data/main';
import { useProject } from '../../services/get-project';
import { ViewDeploymentsTimelineExperienceSuccessTracker } from '../../services/timeline-experience-tracker';
import ChartIndicators from './chart-indicators';
import DeploymentsBar from './deployments-bar';
import IssuePageFetcherNew from './issue-page-fetcher';
import IssuePageFetcherOld from './issue-page-fetcher-old';
import IssueTitle from './issue-title';
import Legend from './legend';
import ListItemContent from './list-item-content';
import ListItemContentTooltip from './list-item-content-tooltip';
import LoadingSkeleton from './loading-skeleton';
import messages from './messages';
import NoMatches from './no-matches';
import { toTableItem, toTableItemProject } from './utils';

const IssuePageFetcher = () =>
	ff('use-ags-for-deployments-timeline_22xgj') ? <IssuePageFetcherNew /> : <IssuePageFetcherOld />;

// @ts-expect-error - TS2315 - Type 'Partial' is not generic.
const useTimelineComponents = (): Partial<Components> => ({
	Bar: DeploymentsBar,
	Loader: IssuePageFetcher,
	NoMatches,
	Legend,
	ChartIndicators,
	IssueTitle,
	ListItemContent,
	ListItemContentTooltip,
});

const Timeline = () => {
	const { data: projectData, loading: projectLoading, error: projectError } = useProject();
	const { data: issuesData, loadingState: issuesLoadingState, error: issuesError } = useIssues();
	const sidebarActions = useSidebarActions();
	const timelineComponents = useTimelineComponents();
	const [selectedIssueKey] = useSidebarIssueKey();
	const [timelineMode] = useTimelineMode();
	const timeWindowFilter = useTimeWindowFilter();
	const endTimelineStartOfDay = startOfDay(timeWindowFilter.before);
	const today = startOfDay(new Date());
	const timelineDuration = getTimelineDurationFromTimeWindowFilter(timeWindowFilter);
	const timelineDefaultPosition = getPositionForTimestamp(endTimelineStartOfDay, timelineDuration);

	// The "adjusted" window will update as new deployments are loaded. This has the effect
	// of moving the left edge of the blanket/overlay back in time as more data is loaded.
	// The intent is to visually indicate the point at which we haven't loaded data for.
	let adjustedTimeWindow = timeWindowFilter;
	if (ff('use-ags-for-deployments-timeline_22xgj')) {
		// eslint-disable-next-line react-hooks/rules-of-hooks -- safe as feature flags won't change in a given browser session
		const earliestVisibleDeploymentDate = useEarliestVisibleDeploymentDate();
		adjustedTimeWindow = {
			// Subtracting one day so the blanket starts just *before* the earliest deployment, rather
			// than literally on top of it.
			after:
				earliestVisibleDeploymentDate === null
					? timeWindowFilter.after
					: max([timeWindowFilter.after, subDays(earliestVisibleDeploymentDate, 1)]),
			before: timeWindowFilter.before,
		};
	}

	// go/jfe-eslint
	// eslint-disable-next-line react-hooks/exhaustive-deps
	const issues = issuesData ?? [];

	// maps issue id to issue key
	const onItemClick = useCallback(
		(clickedId: IssueId) => {
			const clickedIssue = issues.find(({ id }) => id === clickedId);
			if (clickedIssue != null) {
				sidebarActions.showIssue(clickedIssue.key);
			}
		},
		[issues, sidebarActions],
	);

	const selectedItem = issues.find((issue) => issue.key === selectedIssueKey);
	// TODO - passing selectedItemId down from here causes the whole table to re-render
	// would be better to consume selectedIssueKey in ListItem
	const selectedItemId = selectedItem?.id;

	if (issuesLoadingState === LoadingStateOptions.LOADING_INITIAL || projectLoading) {
		return <LoadingSkeleton />;
	}

	if (projectError || !projectData) {
		throw projectError;
	}
	if (issues.length === 0 && issuesError) {
		throw issuesError;
	}

	return (
		<>
			{issues.length > 0 && issuesError && (
				<ErrorFlag
					error={issuesError}
					title={messages.loadingNextIssuePageFailedFlagTitle}
					description={messages.loadingNextIssuePageFailedFlagDescription}
				/>
			)}
			<Table
				key={JSON.stringify(timelineDuration)} // forces re-render to prevent bug where table does not position newly filtered issues correctly
				today={today}
				isHighlightToday
				scope="software-deployments"
				components={timelineComponents}
				timelineDefaultPosition={timelineDefaultPosition}
				timelineMode={timelineMode}
				projects={[toTableItemProject(projectData)]}
				timelineDuration={timelineDuration}
				timelineWindow={adjustedTimeWindow}
				items={issues.map(toTableItem)}
				onItemClick={onItemClick}
				selectedItemId={selectedItemId}
			/>
			<ViewDeploymentsTimelineExperienceSuccessTracker />
			<FireScreenAnalytics />
		</>
	);
};

export { Timeline };
export default Timeline;
